'use client';

// Hero Banner widget — client component.
// 4 variants: carousel (multiple slides), full-bleed (single), split (image + text),
// editorial (text beside two layered photos).
//
// Height, overlay opacity and text placement resolve per breakpoint through
// CSS custom properties (see `.sf-hero*` in globals.css) rather than classes,
// so a merchant can size the hero differently on mobile without this component
// ever measuring the viewport. The variant may also differ per breakpoint;
// that one needs real DOM, so it is handled by rendering one tree per distinct
// variant and letting CSS show the right one.

import { useState, useEffect, useCallback } from 'react';
import { SafeImage } from '@/components/storefront/SafeImage';
import Link from 'next/link';
import { ArrowLeft, ArrowRight, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/storefront/ui/button';
import { IconButton } from '@/components/storefront/ui/icon-button';
import { localizeHref } from '@/lib/storefront/localize-href';
import {
  HERO_LEAD_IMAGE,
  heroCSSVars,
  heroVisibilityClass,
  resolveHeroAt,
  splitPanelTextColors,
} from '@/lib/structured-builder/widget-types/hero-banner';
import type {
  HeroBannerConfig,
  HeroBreakpoint,
  HeroSlide,
  HeroVariant,
} from '@/lib/structured-builder/widget-types/hero-banner';

type Props = {
  config: HeroBannerConfig;
  locale: string;
};

/** `priority` is spent on the first tree only — see HeroBannerClient. */
type VariantProps = Props & { eager: boolean };

// ── Shared: render a single slide's content (heading + subtitle + CTAs) ──

function SlideContent({ slide, config, locale }: { slide: HeroSlide; config: HeroBannerConfig; locale: string }) {
  const isAr = locale === 'ar';
  const heading = isAr ? slide.heading_ar : slide.heading_en;
  const subtitle = isAr ? slide.subtitle_ar : slide.subtitle_en;
  const ctaLabel = isAr ? slide.cta_label_ar : slide.cta_label_en;
  const cta2Label = isAr ? slide.cta2_label_ar : slide.cta2_label_en;
  // Defaults to h2: this hero usually sits below a page's real lead element.
  // A template sets `heading_level: 'h1'` only when the hero IS that lead
  // element (see brand.ts) — see hero-banner.ts for the full rationale.
  const HeadingTag = config.heading_level === 'h1' ? 'h1' : 'h2';

  // Alignment comes from --hero-items-* / --hero-text-align-* so it can differ
  // per breakpoint; `sf-hero-text` is the hook that reads them.
  return (
    <div className="sf-hero-text flex flex-col gap-4 md:gap-6">
      {heading && (
        <HeadingTag
          className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight max-w-2xl"
          style={{ color: config.text_color }}
        >
          {heading}
        </HeadingTag>
      )}
      {subtitle && (
        <p
          className="text-base md:text-lg max-w-xl leading-relaxed"
          style={{ color: config.subtitle_color }}
        >
          {subtitle}
        </p>
      )}
      {(ctaLabel || cta2Label) && (
        <div className="flex items-center gap-3 flex-wrap">
          {ctaLabel && slide.cta_url && (
            <Link href={localizeHref(slide.cta_url, locale)} target={slide.cta_new_tab ? '_blank' : undefined} rel={slide.cta_new_tab ? 'noopener noreferrer' : undefined}>
              <Button variant={slide.cta_variant} color={slide.cta_color} size={slide.cta_size} shape={slide.cta_shape}>
                {ctaLabel}
              </Button>
            </Link>
          )}
          {cta2Label && slide.cta2_url && (
            <Link href={localizeHref(slide.cta2_url, locale)}>
              <Button variant={slide.cta2_variant ?? 'outline'} color={slide.cta2_color ?? 'default'} size={slide.cta_size} shape={slide.cta_shape}>
                {cta2Label}
              </Button>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

/** `object-cover` crops to fill; `object-contain` shows the whole image. */
function fitClass(config: HeroBannerConfig): string {
  return config.image_fit === 'contain' ? 'object-contain' : 'object-cover';
}

// ── Carousel variant ──

function CarouselHero({ config, locale, eager }: VariantProps) {
  const isAr = locale === 'ar';
  // `leaving` is the slide being pushed out, animated off-screen alongside the
  // incoming one; -1 until the first navigation. The enter animation must not
  // run on the first paint: slide 0 is the LCP element, and sliding it in
  // would push the LCP timestamp out by the animation duration.
  // `direction` picks the side the incoming slide enters from. Logical
  // start/end, so RTL mirrors for free.
  const [nav, setNav] = useState<{ active: number; leaving: number; direction: 'next' | 'prev' }>({
    active: 0,
    leaving: -1,
    direction: 'next',
  });
  const { active, leaving, direction } = nav;
  // False during SSR and the first client paint, true from the first effect on.
  // While false only the active slide is in the tree, which is what keeps the
  // LCP candidate unambiguous (see the slide loop below). Flipping it after
  // paint mounts the rest, so advancing never builds an <img> from scratch.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const total = config.slides.length;

  const goTo = useCallback((index: number, dir: 'next' | 'prev') => {
    setNav((n) => (index === n.active ? n : { active: index, leaving: n.active, direction: dir }));
  }, []);

  const next = useCallback(() => {
    setNav((n) => ({ active: (n.active + 1) % total, leaving: n.active, direction: 'next' }));
  }, [total]);
  const prev = useCallback(() => {
    setNav((n) => ({ active: (n.active - 1 + total) % total, leaving: n.active, direction: 'prev' }));
  }, [total]);

  // Autoplay
  useEffect(() => {
    if (!config.autoplay || config.autoplay <= 0 || total <= 1) return;
    const timer = setInterval(next, config.autoplay * 1000);
    return () => clearInterval(timer);
  }, [config.autoplay, total, next]);

  return (
    <div className="sf-hero relative w-full overflow-hidden">
      {/* Slides. Until hydration only the active slide is in the tree: inactive
         slides used to render as opacity-0 layers, which kept N image elements
         alive and meant Lighthouse couldn't pin down a single LCP candidate.
         Holding the first paint to one slide preserves that.
         After hydration every slide mounts and STAYS mounted. Rendering only
         the active slide meant each advance unmounted the <img> and built a new
         one, so slides 1..n downloaded on the first rotation and replayed their
         blur placeholder on every rotation after — cached bytes, but a visible
         reload each cycle. Letting <Image> mount them itself also means the
         warmed cache entry is the exact srcSet candidate it later uses, which
         hand-rolled prefetching cannot guarantee.
         Only slide 0 is `priority`; the rest keep next/image's lazy default, so
         in a breakpoint tree that CSS hides (display:none, no layout box) they
         never download, while in the visible tree they load right after paint. */}
      {config.slides.map((slide, i) => {
        const isActive = i === active;
        const isLeaving = i === leaving;
        if (!hydrated && !isActive) return null;
        // Push transition: the incoming slide enters from one edge while the
        // outgoing one exits the opposite edge, both a full width. The exit
        // holds its end frame (fill-mode-forwards) so it stays off-screen
        // rather than snapping back over the active slide. Under reduced
        // motion there is no animation, so the leaving slide just hides.
        const motion = isActive && leaving !== -1
          ? `animate-in duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:animate-none ${
              direction === 'next' ? 'slide-in-from-end' : 'slide-in-from-start'
            }`
          : isLeaving
            ? `animate-out fill-mode-forwards duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:animate-none motion-reduce:opacity-0 ${
                direction === 'next' ? 'slide-out-to-start' : 'slide-out-to-end'
              }`
            : '';
        return (
          <div
            key={slide.id}
            // Inactive slides stay mounted, so their CTA links would otherwise
            // remain tabbable and screen-reader visible behind an opacity-0
            // layer. `inert` takes the subtree out of both the tab order and the
            // a11y tree; aria-hidden alone would leave the links focusable,
            // which is its own violation. pointer-events-none is belt-and-braces
            // for browsers without inert.
            inert={!isActive}
            className={`absolute inset-0 z-[1] ${
              isActive ? '' : isLeaving ? 'pointer-events-none' : 'pointer-events-none opacity-0'
            } ${motion}`}
          >
            {slide.image && (
              <SafeImage
                src={slide.image}
                alt={isAr ? slide.heading_ar : slide.heading_en}
                fill
                sizes={HERO_LEAD_IMAGE.carousel.sizes}
                quality={i === 0 ? HERO_LEAD_IMAGE.carousel.quality : 85}
                className={fitClass(config)}
                priority={eager && i === 0}
                fetchPriority={eager && i === 0 ? 'high' : 'low'}
                skipBlur={i === 0}
              />
            )}
            <div className="sf-hero-overlay absolute inset-0" style={{ backgroundColor: config.overlay_color }} />
            <div className="sf-hero-content relative z-[2] h-full flex flex-col px-6 md:px-16 lg:px-24">
              <SlideContent slide={slide} config={config} locale={locale} />
            </div>
          </div>
        );
      })}

      {/* Arrows */}
      {config.show_arrows && total > 1 && (
        <>
          {/* start-side button = Previous; its caret points toward the page edge,
             which is left in LTR and right in RTL. end-side = Next, mirrored. */}
          <div className="absolute start-4 top-1/2 -translate-y-1/2 z-[3]">
            <IconButton variant="soft" color="default" size="md" shape="rounded" icon={isAr ? <CaretRight weight="bold" /> : <CaretLeft weight="bold" />} onClick={prev} aria-label="Previous" className="bg-white/80 backdrop-blur-sm" />
          </div>
          <div className="absolute end-4 top-1/2 -translate-y-1/2 z-[3]">
            <IconButton variant="soft" color="default" size="md" shape="rounded" icon={isAr ? <CaretLeft weight="bold" /> : <CaretRight weight="bold" />} onClick={next} aria-label="Next" className="bg-white/80 backdrop-blur-sm" />
          </div>
        </>
      )}

      {/* Dots */}
      {config.show_dots && total > 1 && (
        <div className="absolute bottom-4 inset-x-0 z-[3] flex items-center justify-center gap-2">
          {config.slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i, i > active ? 'next' : 'prev')}
              aria-label={`Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer border-none ${
                i === active ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Full-bleed variant ──

function FullBleedHero({ config, locale, eager }: VariantProps) {
  const isAr = locale === 'ar';
  const slide = config.slides[0];
  if (!slide) return null;

  return (
    // flex-col + flex-1: .sf-hero only has a min-height, so an h-full content
    // box would collapse to its text and text_position could never centre it.
    <div className="sf-hero relative w-full overflow-hidden flex flex-col">
      {slide.image && (
        <SafeImage src={slide.image} alt={isAr ? slide.heading_ar : slide.heading_en} fill sizes={HERO_LEAD_IMAGE['full-bleed'].sizes} quality={HERO_LEAD_IMAGE['full-bleed'].quality} className={fitClass(config)} priority={eager} fetchPriority={eager ? 'high' : 'auto'} />
      )}
      <div className="sf-hero-overlay absolute inset-0" style={{ backgroundColor: config.overlay_color }} />
      <div className="sf-hero-content relative z-[2] flex-1 flex flex-col px-6 md:px-16 lg:px-24">
        <SlideContent slide={slide} config={config} locale={locale} />
      </div>
    </div>
  );
}

// ── Split variant ──

function SplitHero({ config, locale, eager }: VariantProps) {
  const isAr = locale === 'ar';
  const slide = config.slides[0];
  if (!slide) return null;

  const ratioMap: Record<string, [string, string]> = {
    '50-50': ['md:w-1/2', 'md:w-1/2'],
    '60-40': ['md:w-[60%]', 'md:w-[40%]'],
    '40-60': ['md:w-[40%]', 'md:w-[60%]'],
  };
  const [textW, imgW] = ratioMap[config.split_layout] ?? ratioMap['50-50'];
  const imageFirst = config.split_image_side === 'start';
  const panel = splitPanelTextColors(config.split_bg);

  const textSide = (
    <div
      className={`flex flex-col justify-center px-8 md:px-12 lg:px-16 py-12 ${textW}`}
      style={{ backgroundColor: config.split_bg }}
    >
      <SlideContent slide={slide} config={{ ...config, text_color: panel.text, subtitle_color: panel.subtitle }} locale={locale} />
    </div>
  );

  const imageSide = (
    <div className={`relative ${imgW} min-h-[300px]`}>
      {slide.image && (
        <SafeImage src={slide.image} alt={isAr ? slide.heading_ar : slide.heading_en} fill sizes={HERO_LEAD_IMAGE.split.sizes} quality={HERO_LEAD_IMAGE.split.quality} className={fitClass(config)} priority={eager} fetchPriority={eager ? 'high' : 'auto'} />
      )}
    </div>
  );

  return (
    <div className="sf-hero flex flex-col md:flex-row w-full overflow-hidden">
      {imageFirst ? <>{imageSide}{textSide}</> : <>{textSide}{imageSide}</>}
    </div>
  );
}

// ── Editorial variant ──

/**
 * Text beside a collage: the slide image as a tall portrait, `image2` smaller
 * and layered over its lower corner, and an optional chip pinned to the main
 * photo. Photos render at a fraction of the viewport, so ~1000px sources stay
 * sharp where a full-bleed hero would upscale them. The chip sits in the top
 * corner so the inset, which covers the lower half, never hides it. Colours come from the
 * config (the split variant overrides them); on mobile the collage leads.
 */
function EditorialHero({ config, locale, eager }: VariantProps) {
  const isAr = locale === 'ar';
  const slide = config.slides[0];
  if (!slide) return null;

  const pick = (ar?: string, en?: string) => (isAr ? ar : en) || '';
  const heading = pick(slide.heading_ar, slide.heading_en);
  const eyebrow = pick(slide.eyebrow_ar, slide.eyebrow_en);
  const subtitle = pick(slide.subtitle_ar, slide.subtitle_en);
  const ctaLabel = pick(slide.cta_label_ar, slide.cta_label_en);
  const cta2Label = pick(slide.cta2_label_ar, slide.cta2_label_en);
  const chipLabel = pick(slide.chip_label_ar, slide.chip_label_en);
  const HeadingTag = config.heading_level === 'h1' ? 'h1' : 'h2';
  // Points along the reading direction: left in RTL, right in LTR.
  const Arrow = isAr ? ArrowLeft : ArrowRight;
  const imageFirst = config.split_image_side === 'start';

  const collage = (
    <div
      className={`relative mx-auto w-full max-w-[420px] md:max-w-[560px] aspect-[10/11] order-first ${
        imageFirst ? 'md:order-first' : 'md:order-last'
      }`}
    >
      <div aria-hidden className="absolute inset-[12%] rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute top-0 start-0 w-[74%] aspect-[4/5] overflow-hidden rounded-[var(--radius-box)] shadow-xl">
        {slide.image && (
          <SafeImage
            src={slide.image}
            alt={heading}
            fill
            sizes={HERO_LEAD_IMAGE.editorial.sizes}
            quality={HERO_LEAD_IMAGE.editorial.quality}
            className="object-cover"
            priority={eager}
            fetchPriority={eager ? 'high' : 'auto'}
          />
        )}
        {chipLabel && slide.chip_url && (
          <Link
            href={localizeHref(slide.chip_url, locale)}
            className="absolute top-3 start-3 md:top-4 md:start-4 max-w-[calc(100%-1.5rem)] inline-flex items-center gap-2 rounded-full bg-white/90 px-3.5 py-2 text-xs md:text-sm font-medium text-neutral-900 shadow-sm backdrop-blur transition-colors hover:bg-white"
          >
            {chipLabel}
            <Arrow className="h-3.5 w-3.5" weight="bold" />
          </Link>
        )}
      </div>
      {slide.image2 && (
        <div className="absolute bottom-0 end-0 w-[46%] aspect-[4/5] overflow-hidden rounded-[var(--radius-box)] border-[6px] md:border-[10px] border-[color:var(--hero-editorial-bg)] bg-[var(--hero-editorial-bg)] shadow-xl">
          <SafeImage
            src={slide.image2}
            alt=""
            fill
            sizes="(min-width: 1280px) 260px, (min-width: 768px) 22vw, 48vw"
            quality={85}
            className="object-cover"
          />
        </div>
      )}
    </div>
  );

  const text = (
    <div className="flex flex-col items-start gap-5 md:gap-7 text-start">
      {eyebrow && (
        // Letter-spacing and caps only in LTR: tracking breaks Arabic joining.
        <p className="flex items-center gap-3 text-xs md:text-sm font-medium text-primary ltr:uppercase ltr:tracking-[0.18em]">
          <span aria-hidden className="h-px w-8 bg-current" />
          {eyebrow}
        </p>
      )}
      {heading && (
        <HeadingTag
          className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.15] tracking-tight max-w-xl text-balance"
          style={{ color: config.text_color }}
        >
          {heading}
        </HeadingTag>
      )}
      {subtitle && (
        <p className="text-base md:text-lg leading-relaxed max-w-md" style={{ color: config.subtitle_color }}>
          {subtitle}
        </p>
      )}
      {(ctaLabel || cta2Label) && (
        <div className="flex flex-wrap items-center gap-x-7 gap-y-4 pt-1">
          {ctaLabel && slide.cta_url && (
            <Link href={localizeHref(slide.cta_url, locale)} target={slide.cta_new_tab ? '_blank' : undefined} rel={slide.cta_new_tab ? 'noopener noreferrer' : undefined}>
              <Button variant={slide.cta_variant} color={slide.cta_color} size={slide.cta_size} shape={slide.cta_shape}>
                {ctaLabel}
              </Button>
            </Link>
          )}
          {cta2Label && slide.cta2_url && (
            <Link
              href={localizeHref(slide.cta2_url, locale)}
              className="group inline-flex items-center gap-2 text-sm md:text-base font-medium underline-offset-8 hover:underline"
              style={{ color: config.text_color }}
            >
              {cta2Label}
              <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" weight="bold" />
            </Link>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      className="sf-hero flex w-full items-center overflow-hidden"
      style={{ backgroundColor: config.split_bg, '--hero-editorial-bg': config.split_bg } as React.CSSProperties}
    >
      <div className="mx-auto grid w-full max-w-7xl items-center gap-10 px-6 py-10 md:grid-cols-[1fr_1.05fr] md:gap-12 md:px-10 md:py-16 lg:gap-20 lg:px-16">
        {collage}
        {text}
      </div>
    </div>
  );
}

// ── Main component ──

function renderVariant(variant: HeroVariant, props: VariantProps) {
  switch (variant) {
    case 'carousel': return <CarouselHero {...props} />;
    case 'full-bleed': return <FullBleedHero {...props} />;
    case 'split': return <SplitHero {...props} />;
    case 'editorial': return <EditorialHero {...props} />;
  }
}

const ORDER: HeroBreakpoint[] = ['desktop', 'tablet', 'mobile'];

export function HeroBannerClient({ config, locale }: Props) {
  // Which variant each breakpoint resolves to, grouped so a variant shared by
  // two sizes renders once. When all three agree — the default, and the only
  // case before per-breakpoint variants existed — this yields a single group
  // with no visibility class, i.e. exactly one DOM tree and one image.
  const groups = new Map<HeroVariant, HeroBreakpoint[]>();
  for (const bp of ORDER) {
    const v = resolveHeroAt(config, bp).variant;
    const existing = groups.get(v);
    if (existing) existing.push(bp);
    else groups.set(v, [bp]);
  }

  const anyCarousel = [...groups.keys()].includes('carousel');
  const entries = [...groups.entries()];

  return (
    // The CSS vars live on the outer element so every tree below inherits them,
    // and `data-sf-hero` stays on a node that is always in flow — the header's
    // transparent-overlay probe measures it, and a display:none copy would
    // report a zero rect.
    <div
      {...(anyCarousel ? { 'data-sf-hero': '' } : {})}
      style={heroCSSVars(config) as React.CSSProperties}
    >
      {entries.map(([variant, breakpoints], i) => {
        const visibility = heroVisibilityClass(breakpoints);
        const tree = renderVariant(variant, { config, locale, eager: i === 0 });
        return visibility ? (
          <div key={variant} className={visibility}>
            {tree}
          </div>
        ) : (
          <div key={variant}>{tree}</div>
        );
      })}
    </div>
  );
}
